import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth, type AuthSession } from "../auth/context";
import { startSequentialPolling } from "../lib/polling";

export const SIGNUP_POLL_INTERVAL_MS = 10_000;

export function useSignupMembership(paused = false) {
  const { user, session, loading, isCurrentSession, refreshMembership } = useAuth();
  const [revision, setRevision] = useState(0);
  const inFlight = useRef<Promise<void> | null>(null);
  const [failure, setFailure] = useState<{ session: AuthSession; error: unknown } | null>(null);
  const enabled = Boolean(user && session && !loading && !paused);

  useEffect(() => {
    if (!enabled || !session) return;
    return startSequentialPolling({
      intervalMs: SIGNUP_POLL_INTERVAL_MS,
      load: async (signal) => {
        await inFlight.current?.catch(() => {});
        signal.throwIfAborted();
        if (!isCurrentSession(session)) throw new Error("The signed-in account changed.");
        const refresh = refreshMembership({ background: true, signal });
        inFlight.current = refresh;
        try {
          await refresh;
        } finally {
          if (inFlight.current === refresh) inFlight.current = null;
        }
        signal.throwIfAborted();
        if (!isCurrentSession(session)) throw new Error("The signed-in account changed.");
      },
      onSuccess: () => setFailure(null),
      onError: (error) => setFailure({ session, error }),
    });
  }, [enabled, session, isCurrentSession, refreshMembership, revision]);

  const refresh = useCallback(() => setRevision((previous) => previous + 1), []);
  return {
    refresh,
    error: failure?.session === session ? failure.error : null,
  };
}
